import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import './styles/bootstrap.scss';
import './styles/app.scss';
import { formatText } from './constants/texts';
import { useData, useTexts } from './data';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import { RECAPTCHA_SITE_KEY } from './constants/config';
import { string, object } from 'yup';

import {
  searchDonors,
  searchForRejectReasons,
  isPostalSentForYear,
  islocalLocality,
  processdonors,
  findPageNumber,
  ivrStatus,
} from './utility';
import Header from './components/Header';
import Footer from './components/Footer';
import IVRStatus from './components/IVRStatus';
import { useParams } from 'react-router-dom';
import WhatsAppLink from './components/WhatsAppLink';
import CorrectionButton from './components/CorrectionButton';
import DownloadModal from './components/DownloadModal';
import DonorCard from './components/DonorCard';
import tracker from './analytics/tracker';

const localLocality = [
  'China Kaliappa Goundan Valasu',
  'Idayanvalasu',
  'Kondarangi Keeranur',
  'Kulaichinnapatti',
  'Malleswarapuram',
  'Neelangalivalasu',
  'Perumal Goundan Valasu',
  'Velliyan Valasu',
];

function ContentPage() {
  const { id = null } = useParams();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const TEXTS = useTexts();
  const {
    sandha: masterSandha,
    products: masterProducts,
    address: masterPostalAddress,
    masterAddress: masterPostalAddressCopy,
    postalRejectReason: masterRejectReasons,
    pageNos: masterPageNos,
    time: masterTime,
    config,
    ivr: masterMobileIvr,
  } = useData();

  const searchSchema = useMemo(
    () =>
      object({
        mobileNo: string()
          .matches(/^[0-9]{10}$/, TEXTS.MOBILE_VALIDATION_ERROR)
          .required(TEXTS.MOBILE_REQUIRED),
      }),
    [TEXTS]
  );

  const [searchTerm, setSearchTerm] = useState(id);
  const [isLoading, setIsLoading] = useState(false);
  const [enableSearchButton, setEnableSearchButton] = useState(
    !id || id === ''
  );
  const [validationError, setValidationError] = useState('');
  const inputRef = useRef(null);

  const isBypassEnabled = useMemo(() => {
    const bypassCookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith('bypass='))
      ?.split('=')[1];
    return bypassCookie === 'true';
  }, []);

  useEffect(() => {
    tracker.pageViewMain();
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const searchBtnOnClick = useCallback(
    async (e) => {
      e.preventDefault();
      const mobileNo = document.getElementById('mobileNo')?.value?.trim();

      if (!mobileNo) {
        setSearchTerm(null);
        setValidationError('');
        return;
      }

      if (!isBypassEnabled) {
        try {
          await searchSchema.validate({ mobileNo });
          setValidationError('');
        } catch (error) {
          setValidationError(error.message);
          return;
        }
      } else {
        setValidationError('');
      }

      if (!executeRecaptcha) {
        setIsLoading(true);
        try {
          setSearchTerm(mobileNo);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      try {
        await executeRecaptcha('search_donor');
        setSearchTerm(mobileNo);
      } finally {
        setIsLoading(false);
      }
    },
    [executeRecaptcha, isBypassEnabled, searchSchema]
  );

  const addressMap = useMemo(
    () =>
      new Map(
        (Array.isArray(masterPostalAddress) ? masterPostalAddress : []).map(
          (addr) => [addr.donorId, addr]
        )
      ),
    [masterPostalAddress]
  );
  const masterAddressMap = useMemo(
    () =>
      new Map(
        (Array.isArray(masterPostalAddressCopy)
          ? masterPostalAddressCopy
          : []
        ).map((addr) => [addr.donorId, addr])
      ),
    [masterPostalAddressCopy]
  );

  const createDonorData = useCallback(
    (product, type = 'product') => ({
      donorId: product.donorId,
      donorNameTamil: product.donorPrintNameInBook_ta || '',
      featuredLocalityTamil:
        type === 'product' ? product.featuredLocality_ta || '' : '',
      pincode: product.pincode,
      phone: product.donorPhone || '',
      donatedProduct: product.book_printed_products || '',
      fullAddress: product.address || '',
      isPostalSent: isPostalSentForYear(product.donorId, masterPostalAddress),
      isLocalDonor: localLocality.includes(
        islocalLocality(product.donorId, masterPostalAddressCopy)
      ),
      postalReasons: searchForRejectReasons(
        masterRejectReasons,
        product.donorId
      ),
      pageNumber: findPageNumber(product.donorId, masterPageNos?.[type]) || '',
    }),
    [
      masterPostalAddress,
      masterPostalAddressCopy,
      masterRejectReasons,
      masterPageNos,
    ]
  );

  const processedDonors = useMemo(() => {
    if (!searchTerm) return [];
    const productsWithAddress = processdonors(
      searchDonors(masterProducts, searchTerm),
      addressMap,
      masterAddressMap
    );
    return productsWithAddress.map((product) =>
      createDonorData(product, 'product')
    );
  }, [
    searchTerm,
    addressMap,
    masterAddressMap,
    masterProducts,
    createDonorData,
  ]);

  const processedSandhaDonors = useMemo(() => {
    if (!searchTerm) return [];
    const sandhaWithAddress = processdonors(
      searchDonors(masterSandha, searchTerm),
      addressMap,
      masterAddressMap
    );
    return sandhaWithAddress.map((product) =>
      createDonorData(product, 'sandha')
    );
  }, [searchTerm, addressMap, masterAddressMap, masterSandha, createDonorData]);

  const renderDonorCard = useCallback(
    (donor, index) => (
      <DonorCard key={donor.donorId || index} donor={donor} index={index} />
    ),
    []
  );

  const onChangeOnMobileNumber = useCallback(() => {
    const mobileNo = document.getElementById('mobileNo')?.value;
    if (mobileNo === '') {
      setSearchTerm(null);
      setValidationError('');
    }
    setEnableSearchButton(mobileNo === '');
  }, []);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        searchBtnOnClick(e);
        document.getElementById('mobileNo')?.blur();
      }
    },
    [searchBtnOnClick]
  );

  useEffect(() => {
    if (searchTerm === null) return;
    if (processedSandhaDonors.length > 0 || processedDonors.length > 0) {
      tracker.search(
        searchTerm,
        processedSandhaDonors.length,
        processedDonors.length
      );
    } else {
      tracker.donorNotFound(searchTerm);
    }
  }, [searchTerm, processedSandhaDonors.length, processedDonors.length]);

  useEffect(() => {
    const donorName =
      processedSandhaDonors.length > 0
        ? processedSandhaDonors[0]?.donorNameTamil
        : processedDonors.length > 0
          ? processedDonors[0]?.donorNameTamil
          : null;
    const donorId =
      processedSandhaDonors.length > 0
        ? processedSandhaDonors[0]?.donorId
        : processedDonors.length > 0
          ? processedDonors[0]?.donorId
          : null;

    if (donorName) {
      document.title = `${donorId} - ${donorName}`;
    }
  }, [processedSandhaDonors, processedDonors]);

  return (
    <div
      className="pb-2 animate__animated animate__fadeIn"
      data-component="Content"
    >
      <Header />
      <div className="container-fluid">
        <div className="card mt-3">
          <div className="card-body fs-mobile">
            <div className="row g-2">
              <div className="col-12 text-center">
                <h6 className="color fw-bold m-0 functionDate mb-3">
                  {config.functionDate}
                </h6>
                {config?.bookPepareStatus?.[masterTime?.currentStatus] && (
                  <div className="bg-light p-2 rounded border">
                    <small className="text-muted">
                      {masterTime?.currentStatus === '5'
                        ? TEXTS.INVITATION_SENT
                        : config?.bookPepareStatus[masterTime?.currentStatus]}
                    </small>
                  </div>
                )}
                {masterTime?.currentStatus >= 3 && (
                  <div className="row g-2 mb-3 mt-3">
                    <div className="col-12">
                      <DownloadModal
                        link={config?.invitationLink}
                        buttonText={TEXTS.DOWNLOAD_INVITATION}
                        onOpen={() => tracker.downloadInvitation()}
                      />
                    </div>
                    <div className="col-12">
                      <a
                        className="btn btn-success btn-sm w-100"
                        href={`https://wa.me/?text=${encodeURIComponent(TEXTS.WHATSAPP_SHARE_PREFIX)}${encodeURIComponent(config?.invitationLink)}${encodeURIComponent('\n\n' + TEXTS.WHATSAPP_AGENDA_PREFIX)}${encodeURIComponent(config?.agendaLink)}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => tracker.whatsappShareInvitation()}
                      >
                        {TEXTS.SHARE_WHATSAPP}
                      </a>
                    </div>
                    <div className="col-12">
                      <DownloadModal
                        link={config?.agendaLink}
                        buttonText={TEXTS.DOWNLOAD_AGENDA}
                        buttonClassName="btn btn-link btn-sm w-100 text-success"
                        onOpen={() => tracker.downloadAgenda()}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="col-12">
                <div className="input-group">
                  <input
                    type="tel"
                    id="mobileNo"
                    className={`form-control ${validationError ? 'is-invalid' : ''}`}
                    placeholder={TEXTS.SEARCH_PLACEHOLDER}
                    onChange={onChangeOnMobileNumber}
                    ref={inputRef}
                    defaultValue={searchTerm}
                    onKeyDown={handleKeyPress}
                    maxLength={isBypassEnabled ? '20' : '10'}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={searchBtnOnClick}
                    disabled={enableSearchButton}
                  >
                    {TEXTS.SEARCH_BUTTON}
                  </button>
                </div>
                {validationError && (
                  <div className="text-danger small mt-1">
                    {validationError}
                  </div>
                )}
                <div className="text-center mt-1">
                  <small className="text-dark">{TEXTS.SEARCH_HELP}</small>
                </div>
              </div>
            </div>
            {!isLoading &&
            searchTerm !== null &&
            processedDonors.length === 0 &&
            processedSandhaDonors.length === 0 ? (
              <div className="text-center py-4">
                <div className="text-dark mb-2">{TEXTS.NO_DATA_FOUND}</div>
              </div>
            ) : (
              <>
                {processedSandhaDonors.length > 0 && (
                  <div className="row">
                    <div className="col">
                      <h4 className="fw-bold color">{TEXTS.SANDHA_TITLE}</h4>
                    </div>
                    {processedSandhaDonors.length > 1 && (
                      <div className="col-md-12 fw-bold mb-2 blink text-success h6">
                        {formatText(TEXTS.SANDHA_RECORDS_COUNT, {
                          count: processedSandhaDonors.length,
                        })}
                      </div>
                    )}
                    {processedSandhaDonors.map(renderDonorCard)}
                  </div>
                )}
                {processedDonors.length > 0 && (
                  <div className="row">
                    <div className="col">
                      <h4 className="fw-bold color">{TEXTS.DONATION_TITLE}</h4>
                    </div>
                    {processedSandhaDonors.length > 1 && (
                      <div className="col-md-12 fw-bold mb-2 blink text-success h6">
                        {formatText(TEXTS.SANDHA_RECORDS_COUNT, {
                          count: processedSandhaDonors.length,
                        })}
                      </div>
                    )}
                    {processedDonors.map(renderDonorCard)}
                  </div>
                )}
                {ivrStatus(
                  processedSandhaDonors,
                  processedDonors,
                  masterMobileIvr
                ).length !== 0 && (
                  <IVRStatus
                    data={ivrStatus(
                      processedSandhaDonors,
                      processedDonors,
                      masterMobileIvr
                    )}
                  />
                )}

                {(processedSandhaDonors.length > 0 ||
                  processedDonors.length > 0) && (
                  <>
                    <WhatsAppLink
                      sandha={processedSandhaDonors}
                      donors={processedDonors}
                    />
                    <div className="row mt-3">
                      <div className="col-12">
                        <CorrectionButton
                          donors={[
                            ...processedSandhaDonors,
                            ...processedDonors,
                          ]}
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
            {!isLoading && searchTerm === null && (
              <div className="row">
                <div className="text-center py-3 text-dark">
                  <small>{TEXTS.HELP_TEXT}</small>
                </div>
              </div>
            )}
            {isLoading && (
              <div className="text-center py-4">
                <div
                  className="spinner-border spinner-border-sm text-primary me-2"
                  role="status"
                ></div>
                <span className="text-dark">{TEXTS.LOADING_DATA}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default ContentPage;

function Content() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <ContentPage />
    </GoogleReCaptchaProvider>
  );
}

export { Content };
